/**
 * /board/:projectId — read-only Kanban view for a single project (Phase 2a).
 *
 * Auth gate mirrors `routes/index.tsx`: no access token → bounce to /login
 * before kicking off the API roundtrip. The page itself
 * (`features/board/components/BoardPage`) handles 401/403 from the API.
 *
 * This file owns `validateSearch` for the route's URL params; the runtime
 * shape (`BoardSearch`) and the built-in view enum (`BoardViewMode`)
 * live in `@/features/board/use-board-filters` so feature code can
 * import them without creating a circular import back to this route
 * module. The `BoardRouteComponent` reads the parsed search via
 * `Route.useSearch()` and forwards it to the `<BoardPage>` as a prop.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'

import type { DeliverableKind } from '@/api/types-bridge'
import { getAccessToken } from '@/auth/cookie-helpers'
import { BoardPage } from '@/features/board/components/BoardPage'
import { parseListViewSearch } from '@/features/board/components/list/use-list-view-state'
import type { BoardSearch, BoardViewMode } from '@/features/board/use-board-filters'

const KIND_VALUES = new Set<DeliverableKind>(['task', 'ticket', 'event', 'announcement'])
const VIEW_VALUES = new Set<BoardViewMode>(['team-work', 'my-work', 'unassigned', 'review'])

/**
 * Accept either:
 *   - a parsed array (from TanStack Router's JSON deserializer when
 *     `setFilters` writes an array-valued search param);
 *   - a comma-separated string (legacy + human-typed URLs).
 * Filter out unknown kinds and lowercase before comparing.
 */
function parseKinds(raw: unknown): readonly DeliverableKind[] | undefined {
  const candidates: string[] = []
  if (Array.isArray(raw)) {
    for (const v of raw) {
      if (typeof v === 'string') candidates.push(v)
    }
  } else if (typeof raw === 'string' && raw.length > 0) {
    candidates.push(...raw.split(','))
  } else {
    return undefined
  }
  const out: DeliverableKind[] = []
  for (const part of candidates) {
    const trimmed = part.trim().toLowerCase()
    if (KIND_VALUES.has(trimmed as DeliverableKind)) {
      out.push(trimmed as DeliverableKind)
    }
  }
  return out.length > 0 ? out : undefined
}

function parseAssignees(raw: unknown): readonly string[] | undefined {
  const candidates: string[] = []
  if (Array.isArray(raw)) {
    for (const v of raw) {
      if (typeof v === 'string') candidates.push(v)
    }
  } else if (typeof raw === 'string' && raw.length > 0) {
    candidates.push(...raw.split(','))
  } else {
    return undefined
  }
  const out = candidates.map((s) => s.trim()).filter((s) => s.length > 0)
  return out.length > 0 ? out : undefined
}

export const Route = createFileRoute('/board/$projectId')({
  component: BoardRouteComponent,
  validateSearch: (search: Record<string, unknown>): BoardSearch => ({
    // List-view params (`layout`, `sort`, `dir`, `page`, `l*` filters) are
    // parsed by the shared helper so both board surfaces stay in step.
    ...parseListViewSearch(search),
    selectedDeliverable:
      typeof search.selectedDeliverable === 'string' ? search.selectedDeliverable : undefined,
    commentId: typeof search.commentId === 'string' ? search.commentId : undefined,
    parentCommentId:
      typeof search.parentCommentId === 'string' ? search.parentCommentId : undefined,
    view:
      typeof search.view === 'string' && VIEW_VALUES.has(search.view as BoardViewMode)
        ? (search.view as BoardViewMode)
        : undefined,
    kinds: parseKinds(search.kinds),
    assignees: parseAssignees(search.assignees),
    q: typeof search.q === 'string' && search.q.length > 0 ? search.q : undefined,
    create:
      typeof search.create === 'string' && KIND_VALUES.has(search.create as DeliverableKind)
        ? (search.create as DeliverableKind)
        : undefined,
  }),
})

function BoardRouteComponent() {
  const navigate = useNavigate()
  const { projectId } = Route.useParams()
  const search = Route.useSearch()

  useEffect(() => {
    if (!getAccessToken()) {
      navigate({ to: '/login' })
    }
  }, [navigate])

  // Avoid firing the board query until the auth gate has had a chance
  // to redirect — `useBoard` is `enabled` on truthy projectId only,
  // and projectId is always present here, but skipping the render keeps
  // the skeleton from flashing for a logged-out user.
  if (!getAccessToken()) return null

  return (
    <BoardPage
      projectId={projectId}
      search={search}
      initialCommentId={search.commentId ?? null}
      initialParentCommentId={search.parentCommentId ?? null}
      initialCreateKind={search.create ?? null}
    />
  )
}
